import {
  CommentSourceType,
  ContentStatus,
  FlaggedComment
} from '~/containers/ContentModerationContainer/tableColumns';
import {
  ImageQueueResponse,
  ImageScanHistoryResponse
} from '~/containers/ImageModerationContainer/types';
import { emperor } from '~/services';
import { AdminType } from '~/types/admin';
import {
  CsvImportResponse,
  CsvImportResult,
  CsvImportRow
} from '~/components/CsvImportModal/CsvImportModal';

export const fetchAdminsPageRequest = async (params: {
  page: number;
  pageSize: number;
  q?: string;
  role?: string;
}) => {
  return await emperor.get<{ admins: AdminType[]; total: number }>(
    '/v2/admin/all',
    { params }
  );
};

export const updateAdminRoleRequest = async (role: string, userId: string) => {
  return await emperor.patch(`/buildinguser/${userId}`, { role });
};

export const createAdminsRequest = async (
  emails: string[],
  building: string,
  role: string
) => {
  return await emperor.post(`v2/admins`, {
    building,
    emails: emails.filter((e) => !!e),
    role
  });
};

export type ImportPMResult = CsvImportResult & {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  room: string;
  role: string;
  homeProperty: string;
  properties: string;
};

export const importPMs = async (rows: CsvImportRow[], dryRun: boolean) => {
  return await emperor.post<CsvImportResponse<ImportPMResult>>(
    'v2/admins/import',
    { rows, dryRun }
  );
};

export const deleteOneBuildingUserRequest = async (data: {
  buildingId: string;
  userId: string;
}) => {
  return await emperor.post(`/buildinguser/remove`, data);
};

export const updateAdminBuildingsRequest = async (data: {
  user: string;
  buildings: string[];
}) => {
  return await emperor.post('/buildinguser/admin', data);
};

// Matches MODES in api/constants/imageModerationPolicy.js.
export type ImageModerationMode = 'off' | 'shadow' | 'enforce';

// Matches FILTERS in api/controllers/moderation/comments-to-moderate.js.
export type CommentModerationFilter =
  | 'all'
  | 'flagged'
  | 'hidden'
  | 'marketplace'
  | 'containsImages'
  | 'listings';

type GetCommentsToModerateResponse = {
  comments: FlaggedComment[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  counts: Record<CommentModerationFilter, number>;
  imageModerationMode?: ImageModerationMode;
};

// Matches TIERS in api/constants/moderationTier.js.
export type ModerationTier = 'paid' | 'free';

export const getCommentsToModerate = async (params: {
  tier?: ModerationTier;
  filter?: CommentModerationFilter;
  sort?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
}) => {
  return emperor.get<GetCommentsToModerateResponse>(
    '/user/moderation/comments',
    { params }
  );
};

export type ModerationActions = 'MARK_REVIEWED' | 'UPDATE_VISIBILITY';

export const performModerationActionOnComment = async (payload: {
  id: string[];
  visibility?: ContentStatus;
  action: ModerationActions;
  commentType: CommentSourceType;
}) => {
  return emperor.put<GetCommentsToModerateResponse>(
    '/user/moderation/comments',
    payload
  );
};

export const getBuildingNotes = async (buildingId: string) => {
  return emperor.get<any>(`/get-building-notes/${buildingId}`);
};

// ===================== Image moderation ===================== //

export type ImageModerationTab = 'hidden' | 'review';

export type ImageModerationAction = 'HIDE' | 'UNHIDE' | 'MARK_REVIEWED';

export const getImagesToModerate = async (params: {
  tab: ImageModerationTab;
  tier?: ModerationTier;
  building?: string;
  from?: string;
  to?: string;
  category?: string;
  sort?: string;
  direction?: string;
  page?: number;
  pageSize?: number;
}) => {
  return emperor.get<ImageQueueResponse>('/admin/moderation/images', {
    params
  });
};

export const performModerationActionOnImage = async (payload: {
  id: string[];
  action: ImageModerationAction;
}) => {
  return emperor.put<{ message: string; updated: number }>(
    '/admin/moderation/images',
    payload
  );
};

export const getImageScanHistory = async (id: string) => {
  return emperor.get<ImageScanHistoryResponse>(
    `/admin/moderation/images/${id}/scans`
  );
};
